import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { schoolsAPI } from '../../utils/api';
import SchoolNotFound from '../../components/common/SchoolNotFound';

const CATEGORIES = [
  { icon: '🎒', label: 'School Kits', path: '/kits', color: '#dbeafe' },
  { icon: '📚', label: 'Build Your Kit', path: '/products', color: '#d1fae5' },
  { icon: '✏️', label: 'Stationery', path: '/products?category=stationery', color: '#fce7f3' },
  { icon: '📂', label: 'Organizers', path: '/products?category=organizers', color: '#fef3c7' },
  { icon: '🎨', label: 'Art & Craft', path: '/products?category=art-craft', color: '#ffedd5' },
  { icon: '🔬', label: 'Activity Kits', path: '/products?category=activity-kits', color: '#ede9fe' },
];

const CategoryCard = ({ cat }) => {
  const [hovered, setHovered] = useState(false);
  const navigate = useNavigate();

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => navigate(cat.path)}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: '0.6rem', padding: '1.5rem', borderRadius: 14, cursor: 'pointer',
        background: cat.color,
        transform: hovered ? 'translateY(-4px) scale(1.02)' : 'none',
        boxShadow: hovered ? '0 10px 15px rgba(0,0,0,0.05)' : '0 1px 4px rgba(0,0,0,0.07)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      }}
    >
      <span style={{ 
        fontSize: '2.4rem',
        transform: hovered ? 'scale(1.1)' : 'scale(1)',
        transition: 'transform 0.2s ease'
      }}>
        {cat.icon}
      </span>
      <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1f2937' }}>
        {cat.label}
      </span>
    </div>
  );
};


const HomePage = () => {
  const [search, setSearch] = useState('');
  const [schools, setSchools] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dropRef = useRef();

  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (search.length < 2) { setSchools([]); return; }
    setLoading(true);
    const t = setTimeout(() => {
      schoolsAPI.getAll(search)
        .then(r => { setSchools(r.data); setShowDropdown(true); })
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const selectSchool = (school) => {
    setSearch('');
    setShowDropdown(false);
    navigate(`/schools/${school.id}`);
  };

  return (
    <div>
      {/* Hero */}
      <section style={styles.hero}>
        {/* Decorative background elements */}
        <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
          <div style={{ position: 'absolute', top: '-20%', left: '-10%', width: '500px', height: '500px', background: 'rgba(255,255,255,0.08)', borderRadius: '50%', filter: 'blur(60px)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: '-20%', right: '-5%', width: '600px', height: '600px', background: 'rgba(30,58,138,0.4)', borderRadius: '50%', filter: 'blur(80px)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', top: '15%', right: '12%', opacity: 0.15, fontSize: '8rem', transform: 'rotate(15deg)', pointerEvents: 'none', userSelect: 'none' }}>🎒</div>
          <div style={{ position: 'absolute', bottom: '15%', left: '8%', opacity: 0.15, fontSize: '6rem', transform: 'rotate(-15deg)', pointerEvents: 'none', userSelect: 'none' }}>✏️</div>
          <div style={{ position: 'absolute', top: '40%', left: '15%', opacity: 0.1, fontSize: '3rem', transform: 'rotate(25deg)', pointerEvents: 'none', userSelect: 'none' }}>📏</div>
          <div style={{ position: 'absolute', bottom: '30%', right: '18%', opacity: 0.1, fontSize: '4rem', transform: 'rotate(-20deg)', pointerEvents: 'none', userSelect: 'none' }}>✂️</div>
        </div>

        <div className="container text-center" style={{ paddingTop: '3.5rem', paddingBottom: '4.5rem', position: 'relative', zIndex: 1 }}>
          <span style={{ display: 'inline-block', padding: '0.4rem 1.2rem', background: 'rgba(255,255,255,0.15)', borderRadius: '999px', fontSize: '0.85rem', fontWeight: 600, marginBottom: '1.5rem', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.2)' }}>
            ✨ The smart way to shop for back-to-school
          </span>
          <h1 style={styles.heroTitle}>Everything Your Child Needs for School <span style={{display:'inline-block'}}>🎒</span></h1>
          <p style={styles.heroSub}>Find complete, teacher-approved school kits for your exact grade, or shop for individual supplies with just a few clicks.</p>

          {/* Search */}
          <div ref={dropRef} style={{ position: 'relative', maxWidth: 640, margin: '2.5rem auto 0', padding: '0 10px' }}>
            <div style={styles.searchBox} className="hero-search-box">
              <span style={{ fontSize: '1.4rem', marginLeft: '0.5rem', opacity: 0.8 }} className="hide-mobile">🏫</span>
              <input
                style={styles.searchInput}
                className="hero-search-input"
                placeholder="Search by school name, city or zip..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                onFocus={() => schools.length && setShowDropdown(true)}
              />
              <button className="hero-search-btn" style={{ background: '#1e3a8a', color: '#fff', border: 'none', padding: '0.8rem 1.75rem', borderRadius: '999px', fontSize: '1rem', fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 12px rgba(30,58,138,0.3)', transition: 'transform 0.2s, background 0.2s' }} onMouseEnter={e => e.currentTarget.style.background = '#1e40af'} onMouseLeave={e => e.currentTarget.style.background = '#1e3a8a'}>
                {loading ? '...' : 'Find School'}
              </button>
            </div>

            {showDropdown && schools.length > 0 && (
              <div style={styles.dropdown}>
                {schools.map(s => (
                  <div key={s.id} style={styles.dropdownItem} onClick={() => selectSchool(s)} onMouseEnter={e => e.currentTarget.style.background = '#f3f4f6'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#111827' }}>{s.name}</div>
                    <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '0.2rem' }}>
                      {[s.address, s.city, s.state].filter(Boolean).join(', ')} {s.pincode || ''}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {search.length >= 2 && !loading && schools.length === 0 && (
              <div style={{ marginTop: '1rem' }}>
                <SchoolNotFound searchTerm={search} />
              </div>
            )}
          </div>
          
          <div style={{ marginTop: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}>
            <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>or</span>
            <button 
              style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.4)', color: '#fff', padding: '0.6rem 1.5rem', borderRadius: '999px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}
              onClick={() => navigate('/kits')}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.borderColor = '#fff'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; }}
            >
              Browse All School Kits
            </button>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="container" style={{ padding: '2.5rem 1rem' }}>
        <h2 style={styles.sectionTitle}>Shop by Category</h2>
        <div className="grid-3" style={{ marginTop: '1.25rem' }}>
          {CATEGORIES.map(cat => (
            <CategoryCard key={cat.label} cat={cat} />
          ))}
        </div>
      </section>

      {/* Why us */}
      <section style={{ background: '#fff', padding: '2.5rem 0' }}>
        <div className="container">
          {/* Can't find your school */}
          <div style={{ marginBottom: '2rem' }}>
            <SchoolNotFound />
          </div>
          <h2 style={styles.sectionTitle}>Why Parents Love Us</h2>
          <div className="grid-4" style={{ marginTop: '1.25rem' }}>
            {[
              { icon: '🎯', title: 'School-Specific Kits', desc: 'Kits tailored for your exact school & grade' },
              { icon: '✏️', title: 'Customizable', desc: 'Adjust quantities or remove items you already have' },
              { icon: '💰', title: 'Best Prices', desc: 'Bundles are cheaper than buying individually' },
              { icon: '🚚', title: 'Fast Delivery', desc: 'Delivered before school starts' },
            ].map(f => (
              <div key={f.title} className="card text-center" style={{ padding: '1.5rem 1rem' }}>
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>{f.icon}</div>
                <div style={{ fontWeight: 700, marginBottom: '0.3rem' }}>{f.title}</div>
                <div className="text-muted text-sm">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

const styles = {
  hero: { 
    background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', 
    color: '#fff',
    position: 'relative'
  },
  heroTitle: { 
    fontSize: 'clamp(1.8rem, 7vw, 3.5rem)', 
    fontWeight: 900, 
    lineHeight: 1.15, 
    letterSpacing: '-0.02em', 
    textShadow: '0 4px 20px rgba(0,0,0,0.15)' 
  },
  heroSub: { 
    fontSize: 'clamp(0.95rem, 1.8vw, 1.1rem)', 
    opacity: 0.9, 
    maxWidth: '700px', 
    margin: '1.25rem auto 0', 
    lineHeight: 1.6,
    textShadow: '0 2px 10px rgba(0,0,0,0.1)'
  },
  searchBox: {
    display: 'flex', alignItems: 'center', gap: '0.8rem',
    background: '#fff', borderRadius: 999, padding: '0.5rem 0.5rem 0.5rem 1.25rem',
    boxShadow: '0 20px 40px -10px rgba(0,0,0,0.3)',
    transition: 'box-shadow 0.3s ease',
  },
  searchInput: { flex: 1, border: 'none', outline: 'none', fontSize: '1.05rem', color: '#111', background: 'transparent' },
  dropdown: {
    position: 'absolute', top: 'calc(100% + 10px)', left: 0, right: 0,
    background: '#fff', borderRadius: 16, boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
    zIndex: 50, maxHeight: 320, overflowY: 'auto', border: '1px solid #e5e7eb',
    textAlign: 'left'
  },
  dropdownItem: { padding: '1rem 1.25rem', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', transition: 'background 0.15s' },
  sectionTitle: { fontSize: '1.75rem', fontWeight: 800, color: '#111827', letterSpacing: '-0.01em' },
};

export default HomePage;
